/**
 * MealPopularityCard component
 * Displays meal popularity insights with user selection details
 */

import { useTranslation } from 'react-i18next';
import type { MealPopularity } from '@/types/api';
import { capitalize } from '@/lib/utils/text';

export interface MealPopularityCardProps {
  /** Meal popularity data */
  meal: MealPopularity;
  /** Optional click handler to select this meal */
  onSelect?: (mealId: number) => void;
  /** Whether the card is in a loading state */
  isLoading?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Card component displaying meal popularity insights
 *
 * Features:
 * - Shows meal name and tags
 * - Displays total selections count
 * - Lists users who selected this meal
 * - Visual progress bar for popularity
 * - Click to select meal (optional)
 * - Accessible with keyboard navigation
 * - Touch-friendly (44px min height)
 *
 * @example
 * ```tsx
 * function PopularMealsList() {
 *   const { data } = useMenuSuggestions({ weekStartDate: '2025-10-13' });
 *
 *   return (
 *     <div className="space-y-4">
 *       {data.popularMeals.slice(0, 5).map(meal => (
 *         <MealPopularityCard
 *           key={meal.mealId}
 *           meal={meal}
 *           onSelect={(mealId) => console.log('Selected:', mealId)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function MealPopularityCard({
  meal,
  onSelect,
  isLoading = false,
  className = '',
}: MealPopularityCardProps) {
  const { t } = useTranslation();
  const visibleTags = meal.tags.filter((tag) => !tag.isHidden);

  if (isLoading) {
    return (
      <div
        className={`bg-white rounded-lg shadow-md p-4 border border-gray-200 animate-pulse ${className}`}
      >
        <div className="h-6 bg-gray-200 rounded w-3/4 mb-3" />
        <div className="h-4 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="space-y-2">
          <div className="h-3 bg-gray-200 rounded" />
          <div className="h-3 bg-gray-200 rounded w-5/6" />
        </div>
      </div>
    );
  }

  const handleClick = () => {
    if (onSelect) {
      onSelect(meal.mealId);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onSelect(meal.mealId);
    }
  };

  const CardContent = (
    <>
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 text-lg leading-tight mb-1">
            {capitalize(meal.mealName)}
          </h3>
          {visibleTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {visibleTags.slice(0, 3).map((tag) => (
                <span
                  key={tag.id}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700"
                >
                  {capitalize(tag.tag)}
                </span>
              ))}
              {visibleTags.length > 3 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  +{visibleTags.length - 3} {t('menu.more')}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="ml-4 flex-shrink-0 text-right">
          <div className="text-2xl font-bold text-primary-600">
            {meal.totalSelections}
          </div>
          <div className="text-xs text-gray-500">
            {meal.totalSelections === 1 ? t('menu.selection') : t('menu.selections')}
          </div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="mb-3">
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
            style={{ width: `${Math.min(meal.popularityScore, 100)}%` }}
            role="progressbar"
            aria-valuenow={meal.popularityScore}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${meal.popularityScore.toFixed(0)}%`}
          />
        </div>
      </div>

      {/* User selections with enhanced visual badges */}
      <div className="space-y-2">
        <h4 className="text-xs font-semibold text-gray-700 uppercase">
          {t('menu.selectedBy')}
        </h4>
        <div className="flex flex-wrap gap-2">
          {meal.selectedByUsers.map((selection) => (
            <div
              key={selection.userId}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-purple-50 border border-purple-200 transition-all hover:bg-purple-100"
            >
              <span className="font-medium text-purple-900">
                {selection.userName}
              </span>
              {selection.selectionCount > 1 && (
                <span
                  className="text-xs bg-purple-200 text-purple-900 px-1.5 py-0.5 rounded-full font-semibold"
                  title={`Selected ${selection.selectionCount} times`}
                  aria-label={`Selected ${selection.selectionCount} times`}
                >
                  {selection.selectionCount}x
                </span>
              )}
              {selection.isOrganizer && (
                <span
                  className="text-xs bg-yellow-200 text-yellow-900 px-1.5 py-0.5 rounded-full font-semibold"
                  title={t('header.organizer')}
                  aria-label={t('header.organizer')}
                >
                  {t('header.organizer')}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );

  if (onSelect) {
    return (
      <button
        type="button"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={`bg-white rounded-lg shadow-md p-4 border-2 border-gray-200 hover:border-primary-400 hover:shadow-lg transition-all text-left w-full min-h-[44px] focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${className}`}
        aria-label={`Select ${meal.mealName} - ${meal.totalSelections} selections`}
      >
        {CardContent}
      </button>
    );
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-md p-4 border border-gray-200 ${className}`}
    >
      {CardContent}
    </div>
  );
}
